import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, money, SERVICE_TYPE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, Avatar } from "@/components/layout/AppShell";

export default function ServicePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState<any>(null);
  const [settings, setSettings] = useState<any>({});
  const [question, setQuestion] = useState("");
  const [when, setWhen] = useState("");
  const [slots, setSlots] = useState<string[]>([]);
  const [slotDay, setSlotDay] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/services/${id}`).then((r) => { setS(r.data); if (r.data.type === "chat") api.get(`/services/${id}/slots`).then((x) => { setSlots(x.data.slots); setSlotDay(x.data.slots[0]?.slice(0, 10) ? new Date(x.data.slots[0]).toDateString() : ""); }); });
    api.get("/settings/public").then((r) => setSettings(r.data));
  }, [id]);
  if (!s) return <Page><div className="card-tactile h-64 animate-pulse" /></Page>;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const days = Array.from(new Set(slots.map((x) => new Date(x).toDateString())));
  const daySlots = slots.filter((x) => new Date(x).toDateString() === slotDay);

  const book = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return nav(`/login?next=/services/${id}`);
    setBusy(true);
    try {
      const { data: b } = await api.post("/bookings", { service_id: s.id, question, scheduled_at: when || null, timezone: tz });
      const { data: c } = await api.post("/payments/checkout", { kind: "booking", ref_id: b.id, origin_url: window.location.origin });
      window.location.href = c.checkout_url;
    } catch (er) { toast.error(errMsg(er)); setBusy(false); }
  };

  return (
    <Page className="max-w-4xl">
      <div className="grid md:grid-cols-[1.2fr_1fr] gap-8">
        <div>
          <p className="eyebrow mb-3">{SERVICE_TYPE_LABEL[s.type]} reading</p>
          <h1 className="text-4xl sm:text-5xl font-semibold leading-none" data-testid="service-title">{s.title}</h1>
          <Link to={`/@${s.reader?.username}`} className="flex items-center gap-3 mt-5 w-fit"><Avatar u={s.reader} size={40} /><div><p className="serif text-lg leading-none">{s.reader?.name}</p><p className="mono text-[10px] text-[#5E5C56]">@{s.reader?.username} · ★ {s.reader?.rating || "—"}</p></div></Link>
          <p className="mt-6 text-[15px] leading-relaxed text-[#2C2A28] whitespace-pre-wrap">{s.description}</p>
          <dl className="grid grid-cols-2 gap-4 mt-6 text-sm">
            <div className="card-tactile p-4"><dt className="eyebrow">Format</dt><dd className="mt-1">{SERVICE_TYPE_LABEL[s.type]}</dd></div>
            <div className="card-tactile p-4"><dt className="eyebrow">{s.type === "chat" ? "Duration" : "Delivery"}</dt><dd className="mt-1">{s.type === "chat" ? `${s.duration_minutes} minutes` : `Within ${s.delivery_days} days`}</dd></div>
          </dl>
          <p className="text-xs text-[#5E5C56] mt-6">{settings.disclaimer}</p>
        </div>
        <form onSubmit={book} className="card-tactile p-6 h-fit md:sticky md:top-20 space-y-4" data-testid="booking-form">
          <div className="flex items-baseline justify-between"><span className="eyebrow">Price</span><span className="mono text-2xl font-semibold" data-testid="service-price">{money(s.price)}</span></div>
          <div>
            <label className="text-xs font-medium">Your question or focus</label>
            <textarea className="input-ed mt-1 min-h-[120px]" placeholder="What would you like the reader to sit with?" value={question} onChange={(e) => setQuestion(e.target.value)} required minLength={5} data-testid="booking-question" />
          </div>
          {s.type === "chat" && (
            <div data-testid="slot-picker">
              <label className="text-xs font-medium">Session time <span className="mono text-[10px] text-[#5E5C56]">(shown in {tz})</span></label>
              {slots.length === 0 ? <p className="text-sm text-[#5E5C56] mt-2 card-tactile p-3" data-testid="no-slots">This reader hasn't published live availability yet. Message them or choose a written reading.</p> : (<>
                <div className="flex gap-1.5 overflow-x-auto scrollbar-none mt-2 pb-1">{days.map((d) => <button type="button" key={d} onClick={() => { setSlotDay(d); setWhen(""); }} className={`chip whitespace-nowrap ${slotDay === d ? "chip-on" : ""}`} data-testid={`slot-day-${d.replace(/\s/g, "-")}`}>{new Date(d).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</button>)}</div>
                <div className="grid grid-cols-3 gap-1.5 mt-2 max-h-40 overflow-y-auto">{daySlots.map((x) => <button type="button" key={x} onClick={() => setWhen(x)} className={`chip justify-center !py-1.5 mono text-[11px] ${when === x ? "chip-on" : ""}`} data-testid={`slot-${x}`}>{new Date(x).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</button>)}</div>
              </>)}
            </div>
          )}
          <button className="btn-wine w-full" disabled={busy || (s.type === "chat" && !when)} data-testid="book-reading-button">{busy ? "Opening checkout…" : `Book & pay ${money(s.price)}`}</button>
          <p className="text-[11px] text-[#5E5C56] text-center">Secure checkout by Stripe. Your reading room opens once payment clears.</p>
        </form>
      </div>
    </Page>
  );
}
